import { createReviewClient } from './client'
import { createReviewController } from './controller'
import type { BusinessReviewSummary, Review, ReviewReply, UserReviewGroup } from './types'
import { createFetchReviewTransport } from './transport'

export interface ReviewWidgetOptions {
  baseUrl?: string
  contentId: string | number
  contentType?: string
  contentName?: string
  pageSize?: number
  title?: string
  summary?: BusinessReviewSummary | null
  accessToken?: string
  onUnauthorized?: () => void
  currentUserId?: number
  currentUserRole?: string
  showReplyFormByDefault?: boolean
  className?: string
  theme?: ReviewWidgetTheme
  user_email?: string
  userEmail?: string
  userName?: string
}

export interface ReviewWidgetTheme {
  fontFamily?: string
  background?: string
  surface?: string
  surfaceAlt?: string
  borderColor?: string
  textColor?: string
  mutedColor?: string
  primaryColor?: string
  primaryHoverColor?: string
  successColor?: string
  dangerColor?: string
  starColor?: string
  shadow?: string
  cardBackground?: string
  summaryBackground?: string
  inputBackground?: string
  actionBackground?: string
  actionHoverBackground?: string
  ctaBackground?: string
  ctaHoverBackground?: string
  ctaTextColor?: string
  ctaBorderColor?: string
  progressTrackColor?: string
  progressFill?: string
  overlayBackground?: string
  toastBackground?: string
  toastSuccessBackground?: string
  toastErrorBackground?: string
  toastSuccessAccent?: string
  toastErrorAccent?: string
  toastSuccessIconBackground?: string
  toastErrorIconBackground?: string
  modalBackground?: string
  modalBorderColor?: string
  replyBackground?: string
  replyBorderColor?: string
  avatarBackground?: string
  pillBackground?: string
  pillTextColor?: string
  focusRingColor?: string
  radius?: string
}

export interface ReviewWidgetDataAttributes {
  baseUrl?: string
  contentId: string | number
  contentType?: string
  title?: string
  pageSize?: number
  accessToken?: string
  userEmail?: string
}

export interface ReviewWidgetHandle {
  unmount(): void
  refresh(): Promise<void>
}

interface ReviewFormState {
  name: string
  email: string
  rating: number
  content: string
}

interface WidgetLocalState {
  showForm: boolean
  expandedReplyReviewId: number | null
  editingReviewId: number | null
  editingReplyId: number | null
  openedReviewId: number | null
  deleteConfirm: { kind: 'review' | 'reply'; id: number; title: string; message: string } | null
  reviewFormError: string | null
  reviewEditError: string | null
  replyEditError: string | null
  toasts: { id: number; type: 'success' | 'error'; message: string }[]
  form: ReviewFormState
  sessionEmail: string
  replyDrafts: Record<number, string>
  replyEmailDrafts: Record<number, string>
  editReviewDrafts: Record<number, { rating: number; content: string }>
  editReplyDrafts: Record<number, string>
  summary: BusinessReviewSummary | null
}

export interface ReviewWidgetGlobalApi {
  mount(target: string | Element | ArrayLike<Element>, options: ReviewWidgetOptions): ReviewWidgetHandle[]
  autoMount(selector?: string): ReviewWidgetHandle[]
  mountFromDataAttributes(
    target: string | Element | ArrayLike<Element>,
    overrides?: Partial<Omit<ReviewWidgetOptions, 'baseUrl' | 'contentId'>>
  ): ReviewWidgetHandle[]
  unmountAll(): void
}

declare global {
  interface Window {
    ReviewWidget?: ReviewWidgetGlobalApi
    mountReviewWidget?: typeof mountReviewWidget
    mountReviewWidgetFromDataAttributes?: typeof mountReviewWidgetFromDataAttributes
  }
}

const STYLE_ID = 'review-rating-widget-styles'
const LINE_AWESOME_STYLE_ID = 'review-rating-widget-line-awesome'
const LINE_AWESOME_HREF = 'https://maxst.icons8.com/vue-static/landings/line-awesome/line-awesome/1.3.0/css/line-awesome.min.css'
const AUTO_MOUNT_SELECTOR = '.review-widget'
const autoMountedHandles = new Set<ReviewWidgetHandle>()
const DEFAULT_BASE_URL =
  typeof __REVIEW_WIDGET_DEFAULT_BASE_URL__ === 'string'
    ? __REVIEW_WIDGET_DEFAULT_BASE_URL__.trim().replace(/\/$/, '')
    : ''

declare const __REVIEW_WIDGET_DEFAULT_BASE_URL__: string

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function formatDate(value?: string) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date)
}

function initials(name: string) {
  const trimmed = name.trim()
  return (trimmed.charAt(0) || 'U').toUpperCase()
}

function truncateText(value: string, maxLength = 220) {
  const trimmed = value.trim()
  if (trimmed.length <= maxLength) return trimmed
  return `${trimmed.slice(0, maxLength).trimEnd()}...`
}

function starMarkup(activeCount: number, className: string, size = 16) {
  return Array.from({ length: 5 }, (_, index) => {
    const active = index + 1 <= activeCount
    return `
      <span class="${className} ${active ? `${className}--on` : `${className}--off`}">
        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"></path>
        </svg>
      </span>
    `
  }).join('')
}

function actionIconMarkup(kind: 'like' | 'dislike' | 'reply' | 'edit' | 'delete') {
  const icons = {
    like: 'las la-thumbs-up',
    dislike: 'las la-thumbs-down',
    reply: 'las la-reply',
    edit: 'las la-edit',
    delete: 'las la-trash-alt'
  } as const

  return `<i class="rrw-action__icon ${icons[kind]}" aria-hidden="true"></i>`
}

function ratingLabelMarkup(rating: number) {
  return `
    <span class="rrw-rating-label__value">${rating}</span>
    <i class="rrw-rating-label__icon las la-star" aria-hidden="true"></i>
  `
}

function renderRatingPickerMarkup(currentRating: number, action: string, reviewId?: number) {
  const reviewIdAttr = reviewId !== undefined ? ` data-review-id="${reviewId}"` : ""

  return Array.from({ length: 5 }, (_, index) => {
    const rating = index + 1
    const active = rating <= currentRating
    return `
      <button type="button" class="rrw-rating-btn" data-action="${action}"${reviewIdAttr} data-rating="${rating}" aria-label="Rate ${rating} stars">
        <span class="rrw-star ${active ? "rrw-star--on" : "rrw-star--off"}">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"></path>
          </svg>
        </span>
      </button>
    `
  }).join("")
}

function ensureStyles() {
  if (typeof document === 'undefined') return
  if (document.getElementById(STYLE_ID)) return

  if (!document.getElementById(LINE_AWESOME_STYLE_ID)) {
    const lineAwesome = document.createElement('link')
    lineAwesome.id = LINE_AWESOME_STYLE_ID
    lineAwesome.rel = 'stylesheet'
    lineAwesome.href = LINE_AWESOME_HREF
    document.head.appendChild(lineAwesome)
  }

  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `
    .rrw-widget {
      background: var(--rrw-bg);
      --rrw-bg: #ffffff;
      --rrw-surface: #f8fbff;
      --rrw-surface-2: #eef6ff;
      --rrw-border: #d7e6fb;
      --rrw-text: #152238;
      --rrw-muted: #66758f;
      --rrw-primary: #2563eb;
      --rrw-primary-2: #1d4ed8;
      --rrw-success: #0f766e;
      --rrw-danger: #dc2626;
      --rrw-star: #f59e0b;
      --rrw-shadow: 0 18px 48px rgba(21, 34, 56, 0.08);
      --rrw-card-bg: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,251,255,0.98));
      --rrw-summary-bg: linear-gradient(180deg, var(--rrw-surface), #fff);
      --rrw-input-bg: #fff;
      --rrw-action-bg: #eaf1ff;
      --rrw-action-hover-bg: #f8fbff;
      --rrw-cta-bg: #eaf1ff;
      --rrw-cta-hover-bg: #dbeafe;
      --rrw-cta-text: #1d4ed8;
      --rrw-cta-border: #bfdbfe;
      --rrw-progress-track: #e9eef6;
      --rrw-progress-fill: linear-gradient(90deg, #dbeafe, #60a5fa);
      --rrw-overlay-bg: rgba(15, 23, 42, 0.58);
      --rrw-toast-bg: rgba(255, 255, 255, 0.92);
      --rrw-toast-success-bg: linear-gradient(180deg, rgba(240, 253, 244, 0.96), rgba(255, 255, 255, 0.94));
      --rrw-toast-error-bg: linear-gradient(180deg, rgba(254, 242, 242, 0.96), rgba(255, 255, 255, 0.94));
      --rrw-toast-success-accent: #16a34a;
      --rrw-toast-error-accent: #dc2626;
      --rrw-toast-success-icon-bg: rgba(34, 197, 94, 0.12);
      --rrw-toast-error-icon-bg: rgba(239, 68, 68, 0.12);
      --rrw-modal-bg: #fff;
      --rrw-modal-border: rgba(21, 34, 56, 0.12);
      --rrw-reply-bg: #f8fbff;
      --rrw-reply-border: #e1ebf9;
      --rrw-avatar-bg: linear-gradient(135deg, #dbeafe, #bfdbfe);
      --rrw-pill-bg: #eef6ff;
      --rrw-pill-text: #1d4ed8;
      --rrw-focus-ring: rgba(37, 99, 235, 0.12);
      --rrw-radius: 20px;
      font-family: var(--rrw-font-family, Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif);
      color: var(--rrw-text);
    }
    .rrw-widget,
    .rrw-widget * ,
    .rrw-widget *::before,
    .rrw-widget *::after {
      box-sizing: border-box;
    }
    .rrw-card {
      background: var(--rrw-card-bg);
      border: 1px solid var(--rrw-border);
      border-radius: var(--rrw-radius);
      box-shadow: var(--rrw-shadow);
      overflow: hidden;
    }
    .rrw-shell {
      padding: 20px;
    }
    .rrw-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 18px;
    }
    .rrw-title {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
      letter-spacing: -0.02em;
    }
    .rrw-cta {
      border: 1px solid var(--rrw-cta-border);
      border-radius: 999px;
      padding: 10px 14px;
      background: var(--rrw-cta-bg);
      color: var(--rrw-cta-text);
      font-weight: 600;
      cursor: pointer;
    }
    .rrw-cta:hover { background: var(--rrw-cta-hover-bg); }
    .rrw-summary {
      display: grid;
      grid-template-columns: 180px 1fr;
      gap: 20px;
      padding: 18px;
      border-radius: calc(var(--rrw-radius) - 2px);
      background: var(--rrw-summary-bg);
      border: 1px solid var(--rrw-border);
      margin-bottom: 18px;
    }
    .rrw-avg {
      display: flex;
      flex-direction: column;
      gap: 10px;
      align-items: flex-start;
    }
    .rrw-avg-num {
      margin: 0;
      font-size: 52px;
      line-height: 1;
      font-weight: 800;
      letter-spacing: -0.05em;
    }
    .rrw-subtle { color: var(--rrw-muted); font-size: 14px; }
    .rrw-stars { display: inline-flex; gap: 4px; }
    .rrw-star svg { color: var(--rrw-star); fill: currentColor; }
    .rrw-star--off svg { color: #d6deeb; }
    .rrw-dist { display: grid; gap: 8px; align-content: center; }
    .rrw-dist-row {
      display: grid;
      grid-template-columns: 64px 1fr 40px;
      align-items: center;
      gap: 6px;
      font-size: 13px;
    }
    .rrw-dist-label {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: var(--rrw-text);
      font-weight: 700;
      min-width: 0;
    }
    .rrw-rating-label__value {
      font-size: 14px;
      line-height: 1;
    }
    .rrw-rating-label__icon {
      color: var(--rrw-star);
      font-size: 15px;
      line-height: 1;
    }
    .rrw-dist-bar {
      height: 10px;
      background: var(--rrw-progress-track);
      border-radius: 999px;
      overflow: hidden;
    }
    .rrw-dist-fill {
      height: 100%;
      border-radius: inherit;
      background: var(--rrw-progress-fill);
    }
    .rrw-form {
      border: 1px solid var(--rrw-border);
      background: var(--rrw-surface);
      border-radius: 18px;
      padding: 16px;
      margin-bottom: 18px;
    }
    .rrw-form[data-form="review-edit"],
    .rrw-reply-form[data-form="reply-edit"] {
      position: fixed;
      inset: 0;
      z-index: 9999;
      margin: 0;
      padding: 16px;
      border: 0;
      border-radius: 0;
      background: var(--rrw-overlay-bg);
      overflow: auto;
    }
    .rrw-form[data-form="review-edit"] {
      display: grid;
      place-items: center;
    }
    .rrw-form[data-form="review-edit"] .rrw-form-grid {
      width: min(680px, 100%);
      margin: auto;
      padding: 20px;
      border-radius: var(--rrw-radius);
      background: var(--rrw-modal-bg);
      box-shadow: 0 28px 70px rgba(15, 23, 42, 0.32);
      border: 1px solid var(--rrw-modal-border);
    }
    .rrw-reply-form[data-form="reply-edit"] {
      display: grid;
      gap: 12px;
      align-content: center;
      justify-items: center;
    }
    .rrw-reply-form[data-form="reply-edit"] .rrw-reply-modal {
      width: min(680px, 100%);
    }
    .rrw-reply-form[data-form="reply-edit"] .rrw-reply-modal {
      background: var(--rrw-modal-bg);
      box-shadow: 0 28px 70px rgba(15, 23, 42, 0.32);
      border: 1px solid var(--rrw-modal-border);
      border-radius: var(--rrw-radius);
      padding: 20px;
      display: grid;
      gap: 16px;
    }
    .rrw-reply-form[data-form="reply-edit"] .rrw-reply-modal__head {
      display: flex;
      align-items: start;
      justify-content: space-between;
      gap: 12px;
    }
    .rrw-reply-form[data-form="reply-edit"] .rrw-reply-modal__title {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      letter-spacing: -0.02em;
      color: var(--rrw-text);
    }
    .rrw-reply-form[data-form="reply-edit"] .rrw-reply-modal__subtle {
      margin: 4px 0 0;
      font-size: 14px;
      line-height: 1.45;
      color: var(--rrw-muted);
    }
    .rrw-reply-form[data-form="reply-edit"] .rrw-reply-modal__meta {
      display: flex;
      flex-wrap: wrap;
      gap: 8px 12px;
      color: #475569;
      font-size: 13px;
    }
    .rrw-reply-form[data-form="reply-edit"] .rrw-reply-modal__meta span {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 10px;
      border-radius: 999px;
      background: var(--rrw-surface);
      border: 1px solid rgba(15, 23, 42, 0.08);
    }
    .rrw-reply-form[data-form="reply-edit"] .rrw-reply-modal__body {
      display: grid;
      gap: 10px;
    }
    .rrw-reply-form[data-form="reply-edit"] .rrw-textarea,
    .rrw-reply-form[data-form="reply-edit"] .rrw-actions {
      width: 100%;
    }
    .rrw-reply-form[data-form="reply-edit"] .rrw-textarea {
      min-height: 220px;
      background: var(--rrw-input-bg);
      border: 1px solid var(--rrw-modal-border);
      border-radius: 16px;
      padding: 16px;
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
    }
    .rrw-reply-form[data-form="reply-edit"] .rrw-actions {
      justify-content: flex-end;
      margin-top: -2px;
    }
    .rrw-form-grid { display: grid; gap: 12px; }
    .rrw-label {
      display: block;
      margin-bottom: 6px;
      font-size: 14px;
      font-weight: 600;
      color: var(--rrw-muted);
    }
    .rrw-input,
    .rrw-textarea {
      width: 100%;
      max-width: 100%;
      border: 1px solid var(--rrw-modal-border);
      border-radius: 12px;
      background: var(--rrw-input-bg);
      padding: 11px 12px;
      outline: none;
      color: var(--rrw-text);
      font-size: 14px;
    }
    .rrw-textarea {
      display: block;
      min-height: 120px;
      resize: vertical;
    }
    .rrw-input:focus,
    .rrw-textarea:focus {
      border-color: var(--rrw-primary);
      box-shadow: 0 0 0 4px var(--rrw-focus-ring);
    }
    .rrw-rating-pick { display: inline-flex; gap: 6px; }
    .rrw-rating-btn {
      border: 0;
      background: transparent;
      padding: 0;
      cursor: pointer;
      line-height: 0;
    }
    .rrw-actions {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      margin-top: 4px;
      flex-wrap: wrap;
    }
    .rrw-btn {
      border: 0;
      border-radius: 999px;
      padding: 10px 14px;
      font-weight: 600;
      cursor: pointer;
    }
    .rrw-btn--primary { background: var(--rrw-primary); color: #fff; }
    .rrw-btn--ghost { background: var(--rrw-action-bg); color: var(--rrw-primary); }
    .rrw-btn--danger { background: rgba(254, 226, 226, 0.95); color: var(--rrw-danger); }
    .rrw-list { display: grid; gap: 12px; }
    .rrw-empty {
      padding: 20px;
      text-align: center;
      color: var(--rrw-muted);
      border: 1px dashed var(--rrw-border);
      border-radius: 16px;
      background: var(--rrw-input-bg);
    }
    .rrw-item {
      display: grid;
      grid-template-columns: 44px 1fr;
      gap: 12px;
      padding: 16px;
      border: 1px solid var(--rrw-border);
      border-radius: var(--rrw-radius);
      background: var(--rrw-input-bg);
    }
    .rrw-avatar {
      width: 44px;
      height: 44px;
      border-radius: 999px;
      display: grid;
      place-items: center;
      font-weight: 800;
      color: var(--rrw-text);
      background: var(--rrw-avatar-bg);
      flex: 0 0 auto;
    }
    .rrw-top {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
      margin-bottom: 8px;
    }
    .rrw-name {
      margin: 0;
      font-weight: 700;
      font-size: 15px;
    }
    .rrw-date { margin-top: 4px; font-size: 13px; color: var(--rrw-muted); }
    .rrw-body { margin: 10px 0 0; line-height: 1.6; color: var(--rrw-text); }
    .rrw-inline-link {
      border: 0;
      background: transparent;
      color: var(--rrw-primary);
      font: inherit;
      font-weight: 700;
      padding: 0;
      cursor: pointer;
    }
    .rrw-inline-link:hover { text-decoration: underline; }
    .rrw-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      margin-top: 8px;
    }
    .rrw-pill {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      border-radius: 999px;
      background: var(--rrw-pill-bg);
      color: var(--rrw-pill-text);
      font-size: 12px;
      padding: 4px 8px;
    }
    .rrw-review-actions,
    .rrw-reply-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
    .rrw-action {
      border: 1px solid var(--rrw-border);
      background: var(--rrw-input-bg);
      border-radius: 999px;
      padding: 8px 10px;
      font-size: 13px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 7px;
      color: var(--rrw-text);
      line-height: 1;
    }
    .rrw-action:hover { background: var(--rrw-action-hover-bg); }
    .rrw-action--icon {
      width: 36px;
      height: 36px;
      justify-content: center;
      padding: 0;
    }
    .rrw-action--danger {
      color: var(--rrw-danger);
      background: rgba(255, 245, 245, 0.96);
    }
    .rrw-action--danger:hover { background: rgba(254, 236, 236, 0.98); }
    .rrw-action__icon {
      width: 16px;
      height: 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      font-size: 16px;
      line-height: 1;
    }
    .rrw-action__count {
      min-width: 1ch;
      font-weight: 700;
      font-size: 12px;
    }
    .rrw-replies {
      margin-top: 14px;
      padding-left: 14px;
      border-left: 2px solid var(--rrw-border);
      display: grid;
      gap: 10px;
    }
    .rrw-reply {
      display: grid;
      grid-template-columns: 36px 1fr;
      gap: 10px;
      padding: 12px;
      border-radius: 14px;
      background: var(--rrw-reply-bg);
      border: 1px solid var(--rrw-reply-border);
    }
    .rrw-reply-avatar {
      width: 36px;
      height: 36px;
      border-radius: 999px;
      display: grid;
      place-items: center;
      background: var(--rrw-avatar-bg);
      font-weight: 700;
    }
    .rrw-reply-text { margin: 8px 0 0; line-height: 1.55; color: var(--rrw-text); }
    .rrw-reply-form {
      display: grid;
      gap: 10px;
      margin-top: 10px;
    }
    .rrw-loading {
      padding: 24px;
      text-align: center;
      color: var(--rrw-muted);
    }
    .rrw-more {
      display: flex;
      justify-content: center;
      margin-top: 6px;
    }
    .rrw-error {
      padding: 14px;
      border-radius: 12px;
      background: rgba(254, 242, 242, 0.98);
      color: var(--rrw-danger);
      border: 1px solid rgba(254, 202, 202, 0.9);
      margin-bottom: 14px;
    }
    .rrw-toast-stack {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      display: grid;
      gap: 10px;
      width: min(380px, calc(100vw - 40px));
      pointer-events: none;
    }
    .rrw-toast {
      pointer-events: auto;
      display: grid;
      grid-template-columns: 28px 1fr auto;
      align-items: start;
      gap: 12px;
      padding: 14px 14px 14px 12px;
      border-radius: 18px;
      border: 1px solid rgba(21, 34, 56, 0.12);
      box-shadow: 0 18px 44px rgba(15, 23, 42, 0.14);
      background: var(--rrw-toast-bg);
      color: var(--rrw-text);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      position: relative;
      overflow: hidden;
      animation: rrwToastIn 240ms cubic-bezier(0.16, 1, 0.3, 1) both;
      transition: transform 180ms ease, box-shadow 180ms ease;
    }
    .rrw-toast::before {
      content: "";
      position: absolute;
      inset: 0 auto 0 0;
      width: 4px;
      background: transparent;
    }
    .rrw-toast:hover {
      transform: translateY(-1px);
      box-shadow: 0 22px 50px rgba(15, 23, 42, 0.18);
    }
    .rrw-toast--success {
      background: var(--rrw-toast-success-bg);
      border-color: rgba(187, 247, 208, 0.9);
    }
    .rrw-toast--error {
      background: var(--rrw-toast-error-bg);
      border-color: rgba(254, 202, 202, 0.9);
    }
    .rrw-toast--success::before {
      background: var(--rrw-toast-success-accent);
    }
    .rrw-toast--error::before {
      background: var(--rrw-toast-error-accent);
    }
    .rrw-toast__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      margin-top: 0;
      font-size: 20px;
      line-height: 1;
      border-radius: 999px;
      background: rgba(37, 99, 235, 0.08);
      color: var(--rrw-primary);
    }
    .rrw-toast--success .rrw-toast__icon {
      color: var(--rrw-toast-success-accent);
      background: var(--rrw-toast-success-icon-bg);
    }
    .rrw-toast--error .rrw-toast__icon {
      color: var(--rrw-toast-error-accent);
      background: var(--rrw-toast-error-icon-bg);
    }
    .rrw-toast__message {
      font-size: 14px;
      line-height: 1.45;
      color: inherit;
      padding-top: 2px;
    }
    .rrw-toast__close {
      border: 0;
      background: transparent;
      color: var(--rrw-muted);
      cursor: pointer;
      line-height: 1;
      padding: 0;
      font-size: 18px;
      opacity: 0.8;
      transition: opacity 160ms ease, transform 160ms ease;
    }
    .rrw-toast__close:hover {
      opacity: 1;
      transform: scale(1.06);
    }
    @keyframes rrwToastIn {
      from {
        opacity: 0;
        transform: translate3d(0, -12px, 0) scale(0.98);
      }
      to {
        opacity: 1;
        transform: translate3d(0, 0, 0) scale(1);
      }
    }
    .rrw-modal-overlay {
      position: fixed;
      inset: 0;
      background: var(--rrw-overlay-bg);
      display: grid;
      place-items: center;
      padding: 16px;
      z-index: 9999;
    }
    .rrw-modal {
      width: min(680px, 100%);
      max-height: calc(100vh - 32px);
      overflow: auto;
      background: var(--rrw-modal-bg);
      border-radius: var(--rrw-radius);
      border: 1px solid var(--rrw-modal-border);
      box-shadow: 0 28px 70px rgba(15, 23, 42, 0.32);
      padding: 20px;
    }
    .rrw-modal__body {
      display: grid;
      gap: 12px;
    }
    .rrw-modal__content {
      margin: 0;
      line-height: 1.7;
      white-space: pre-wrap;
      color: var(--rrw-text);
    }
    .rrw-modal__subtle {
      color: var(--rrw-muted);
      font-size: 13px;
    }
    .rrw-modal__head {
      display: flex;
      align-items: start;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 16px;
    }
    .rrw-modal__title {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      letter-spacing: -0.02em;
    }
    .rrw-modal__close {
      border: 0;
      background: transparent;
      color: var(--rrw-muted);
      cursor: pointer;
      font-size: 18px;
      line-height: 1;
      padding: 4px;
    }
    .rrw-sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
    @media (max-width: 720px) {
      .rrw-summary { grid-template-columns: 1fr; }
      .rrw-item { grid-template-columns: 1fr; }
      .rrw-avatar { width: 40px; height: 40px; }
      .rrw-toast-stack {
        left: 16px;
        right: 16px;
        width: auto;
      }
    }
  `
  document.head.appendChild(style)
}

function applyThemeToRoot(root: HTMLElement, theme?: ReviewWidgetTheme) {
  if (!theme) return

  const setVar = (name: string, value: string | undefined) => {
    if (value && value.trim()) {
      root.style.setProperty(name, value)
    }
  }

  setVar('--rrw-font-family', theme.fontFamily)
  setVar('--rrw-bg', theme.background)
  setVar('--rrw-surface', theme.surface)
  setVar('--rrw-surface-2', theme.surfaceAlt)
  setVar('--rrw-border', theme.borderColor)
  setVar('--rrw-text', theme.textColor)
  setVar('--rrw-muted', theme.mutedColor)
  setVar('--rrw-primary', theme.primaryColor)
  setVar('--rrw-primary-2', theme.primaryHoverColor)
  setVar('--rrw-success', theme.successColor)
  setVar('--rrw-danger', theme.dangerColor)
  setVar('--rrw-star', theme.starColor)
  setVar('--rrw-shadow', theme.shadow)
  setVar('--rrw-card-bg', theme.cardBackground)
  setVar('--rrw-summary-bg', theme.summaryBackground)
  setVar('--rrw-input-bg', theme.inputBackground)
  setVar('--rrw-action-bg', theme.actionBackground)
  setVar('--rrw-action-hover-bg', theme.actionHoverBackground)
  setVar('--rrw-cta-bg', theme.ctaBackground)
  setVar('--rrw-cta-hover-bg', theme.ctaHoverBackground)
  setVar('--rrw-cta-text', theme.ctaTextColor)
  setVar('--rrw-cta-border', theme.ctaBorderColor)
  setVar('--rrw-progress-track', theme.progressTrackColor)
  setVar('--rrw-progress-fill', theme.progressFill)
  setVar('--rrw-overlay-bg', theme.overlayBackground)
  setVar('--rrw-toast-bg', theme.toastBackground)
  setVar('--rrw-toast-success-bg', theme.toastSuccessBackground)
  setVar('--rrw-toast-error-bg', theme.toastErrorBackground)
  setVar('--rrw-toast-success-accent', theme.toastSuccessAccent)
  setVar('--rrw-toast-error-accent', theme.toastErrorAccent)
  setVar('--rrw-toast-success-icon-bg', theme.toastSuccessIconBackground)
  setVar('--rrw-toast-error-icon-bg', theme.toastErrorIconBackground)
  setVar('--rrw-modal-bg', theme.modalBackground)
  setVar('--rrw-modal-border', theme.modalBorderColor)
  setVar('--rrw-reply-bg', theme.replyBackground)
  setVar('--rrw-reply-border', theme.replyBorderColor)
  setVar('--rrw-avatar-bg', theme.avatarBackground)
  setVar('--rrw-pill-bg', theme.pillBackground)
  setVar('--rrw-pill-text', theme.pillTextColor)
  setVar('--rrw-focus-ring', theme.focusRingColor)
  setVar('--rrw-radius', theme.radius)
}

function parseDataAttributes(element: Element): ReviewWidgetDataAttributes | null {
  const htmlElement = element as HTMLElement
  const baseUrl = htmlElement.dataset.baseUrl?.trim()
  const contentIdRaw = htmlElement.dataset.contentId?.trim()
  const contentType = htmlElement.dataset.contentType?.trim()

  if (!contentIdRaw) return null

  const parsedContentId = Number.isFinite(Number(contentIdRaw)) ? Number(contentIdRaw) : contentIdRaw
  const parsedPageSize = htmlElement.dataset.pageSize ? Number(htmlElement.dataset.pageSize) : undefined

  return {
    ...(baseUrl ? { baseUrl } : {}),
    contentId: parsedContentId,
    ...(contentType ? { contentType } : {}),
    ...(htmlElement.dataset.title ? { title: htmlElement.dataset.title } : {}),
    ...(htmlElement.dataset.accessToken ? { accessToken: htmlElement.dataset.accessToken } : {}),
    ...(htmlElement.dataset.userEmail ? { userEmail: htmlElement.dataset.userEmail } : {}),
    ...(parsedPageSize && Number.isFinite(parsedPageSize) ? { pageSize: parsedPageSize } : {})
  }
}

function findReview(groups: UserReviewGroup[], reviewId: number) {
  for (const group of groups) {
    const review = group.reviews.find((item) => item.id === reviewId)
    if (review) {
      return { group, review }
    }
  }
  return null
}

function findReply(groups: UserReviewGroup[], replyId: number) {
  for (const group of groups) {
    for (const review of group.reviews) {
      const reply = review.replies?.find((item) => item.id === replyId)
      if (reply) {
        return { group, review, reply }
      }
    }
  }
  return null
}

class ReviewWidgetInstance implements ReviewWidgetHandle {
  private readonly controller
  private readonly client
  private readonly unsubscribe
  private readonly root: HTMLElement
  private uiState: WidgetLocalState
  private toastTimers = new Map<number, number>()
  private toastSeq = 0
  private destroyed = false
  private isSubmitting = false

  constructor(private readonly options: ReviewWidgetOptions, root: HTMLElement) {
    ensureStyles()
    this.root = root
    this.root.classList.add('rrw-widget')
    if (options.className) {
      for (const className of options.className.split(/\s+/).filter(Boolean)) {
        this.root.classList.add(className)
      }
    }
    applyThemeToRoot(this.root, options.theme)
    this.root.innerHTML = `<div class="rrw-card"><div class="rrw-shell"><div class="rrw-loading">Loading reviews...</div></div></div>`
    const configuredEmail = this.resolveConfiguredEmail(options)
    const contentType = options.contentType?.trim() || 'business'

    const resolveAccessToken = () => options.accessToken ?? null

    const transport = createFetchReviewTransport({
      baseUrl: resolveBaseUrl(options.baseUrl),
      getAccessToken: resolveAccessToken,
      ...(options.onUnauthorized ? { onUnauthorized: options.onUnauthorized } : {})
    })

    const client = createReviewClient({ transport })
    this.client = client
    this.controller = createReviewController({
      client,
      contentId: options.contentId,
      ...(options.pageSize ? { pageSize: options.pageSize } : {})
    })

    this.uiState = {
      showForm: false,
      expandedReplyReviewId: options.showReplyFormByDefault ? 1 : null,
      editingReviewId: null,
      editingReplyId: null,
      openedReviewId: null,
      deleteConfirm: null,
      reviewFormError: null,
      reviewEditError: null,
      replyEditError: null,
      toasts: [],
      form: {
        name: options.userName ?? "",
        email: configuredEmail,
        rating: 5,
        content: ""
      },
      sessionEmail: configuredEmail,
      replyDrafts: {},
      replyEmailDrafts: {},
      editReviewDrafts: {},
      editReplyDrafts: {},
      summary: options.summary ?? null
    }

    this.bindEvents()
    this.unsubscribe = this.controller.subscribe(() => {
      this.render()
    })

    void this.loadSummary()
    void this.controller.loadReviews()
  }

  private resolveConfiguredEmail(options: Pick<ReviewWidgetOptions, 'user_email' | 'userEmail'>) {
    return (options.user_email ?? options.userEmail ?? '').trim()
  }

  private resolveUserEmail(preferred?: string) {
    const preferredEmail = preferred?.trim() ?? ''
    if (preferredEmail) return preferredEmail

    const sessionEmail = this.uiState.sessionEmail.trim()
    if (sessionEmail) return sessionEmail

    return this.resolveConfiguredEmail(this.options)
  }

  private bindEvents() {
    this.root.addEventListener('click', this.handleClick)
    this.root.addEventListener('input', this.handleInput)
    this.root.addEventListener('submit', this.handleSubmit)
  }

  private unbindEvents() {
    this.root.removeEventListener('click', this.handleClick)
    this.root.removeEventListener('input', this.handleInput)
    this.root.removeEventListener('submit', this.handleSubmit)
  }

  private pushToast(message: string, type: 'success' | 'error' = 'success') {
    if (this.destroyed) return

    const id = ++this.toastSeq
    this.uiState.toasts = [...this.uiState.toasts, { id, type, message }]
    this.render()

    const timer = window.setTimeout(() => {
      this.toastTimers.delete(id)
      this.uiState.toasts = this.uiState.toasts.filter((toast) => toast.id !== id)
      if (!this.destroyed) {
        this.render()
      }
    }, 3000)

    this.toastTimers.set(id, timer)
  }

  private clearToast(id: number) {
    const timer = this.toastTimers.get(id)
    if (timer != null) {
      window.clearTimeout(timer)
      this.toastTimers.delete(id)
    }
    this.uiState.toasts = this.uiState.toasts.filter((toast) => toast.id !== id)
    if (!this.destroyed) {
      this.render()
    }
  }

  private handleClick = async (event: MouseEvent) => {
    const target = event.target as HTMLElement | null
    const actionEl = target?.closest<HTMLElement>("[data-action]")
    if (!actionEl || !this.root.contains(actionEl)) return

    const action = actionEl.dataset.action
    const reviewId = Number(actionEl.dataset.reviewId || "")
    const replyId = Number(actionEl.dataset.replyId || "")

    if (action === "toggle-form") {
      this.uiState.showForm = !this.uiState.showForm
      if (!this.uiState.showForm) {
        this.uiState.reviewFormError = null
      }
      this.render()
      return
    }

    if (action === "set-rating") {
      const rating = Number(actionEl.dataset.rating || 5)
      this.uiState.form.rating = rating
      this.render()
      return
    }

    if (action === "set-review-edit-rating" && Number.isFinite(reviewId)) {
      const draft = this.uiState.editReviewDrafts[reviewId]
      if (draft) {
        draft.rating = Number(actionEl.dataset.rating || draft.rating)
        this.render()
      }
      return
    }

    if (action === "toggle-replies" && Number.isFinite(reviewId)) {
      this.uiState.expandedReplyReviewId =
        this.uiState.expandedReplyReviewId === reviewId ? null : reviewId
      this.render()
      return
    }

    if (action === "open-review-modal" && Number.isFinite(reviewId)) {
      this.uiState.openedReviewId = reviewId
      this.render()
      return
    }

    if (action === "close-review-modal") {
      this.uiState.openedReviewId = null
      this.render()
      return
    }

    if (action === "like-review" && Number.isFinite(reviewId)) {
      const found = findReview(this.controller.getState().reviewGroups, reviewId)
      if (!found) return

      try {
        const userEmail = this.resolveUserEmail()
        if (!userEmail) {
          this.pushToast('Email is required to update helpful status', 'error')
          return
        }
        await this.controller.markHelpful(reviewId, userEmail)
        const review = found.review
        const nextPatch: Partial<Review> = {}

        if (review.user_liked) {
          nextPatch.likes_count = Math.max((review.likes_count || 0) - 1, 0)
          nextPatch.user_liked = 0
          this.pushToast('Removed helpful mark')
        } else {
          nextPatch.likes_count = (review.likes_count || 0) + 1
          nextPatch.user_liked = 1
          if (review.user_disliked) {
            nextPatch.dislikes_count = Math.max((review.dislikes_count || 0) - 1, 0)
            nextPatch.user_disliked = 0
          }
          this.pushToast('Marked as helpful')
        }

        this.controller.patchReview(reviewId, nextPatch)
      } catch (error) {
        this.pushToast(
          error instanceof Error ? error.message : 'Failed to update like status',
          'error'
        )
      }
      return
    }

    if (action === "dislike-review" && Number.isFinite(reviewId)) {
      const found = findReview(this.controller.getState().reviewGroups, reviewId)
      if (!found) return

      try {
        const userEmail = this.resolveUserEmail()
        if (!userEmail) {
          this.pushToast('Email is required to update dislike status', 'error')
          return
        }
        await this.controller.markDislike(reviewId, userEmail)
        const review = found.review
        const nextPatch: Partial<Review> = {}

        if (review.user_disliked) {
          nextPatch.dislikes_count = Math.max((review.dislikes_count || 0) - 1, 0)
          nextPatch.user_disliked = 0
          this.pushToast('Removed dislike mark')
        } else {
          nextPatch.dislikes_count = (review.dislikes_count || 0) + 1
          nextPatch.user_disliked = 1
          if (review.user_liked) {
            nextPatch.likes_count = Math.max((review.likes_count || 0) - 1, 0)
            nextPatch.user_liked = 0
          }
          this.pushToast('Marked as disliked')
        }

        this.controller.patchReview(reviewId, nextPatch)
      } catch (error) {
        this.pushToast(
          error instanceof Error ? error.message : 'Failed to update dislike status',
          'error'
        )
      }
      return
    }

    if (action === "load-more") {
      await this.controller.loadMore()
      return
    }

    if (action === "toggle-review-edit" && Number.isFinite(reviewId)) {
      const found = findReview(this.controller.getState().reviewGroups, reviewId)
      if (!found) return

      this.uiState.editingReviewId = this.uiState.editingReviewId === reviewId ? null : reviewId
      if (this.uiState.editingReviewId === reviewId) {
        this.uiState.editReviewDrafts[reviewId] = {
          rating: Number(found.review.rating || 0),
          content: found.review.content || ""
        }
        this.uiState.reviewEditError = null
      } else {
        delete this.uiState.editReviewDrafts[reviewId]
        this.uiState.reviewEditError = null
      }
      this.render()
      return
    }

    if (action === "cancel-review-edit" && Number.isFinite(reviewId)) {
      delete this.uiState.editReviewDrafts[reviewId]
      if (this.uiState.editingReviewId === reviewId) {
        this.uiState.editingReviewId = null
      }
      this.render()
      return
    }

    if (action === "toggle-reply-edit" && Number.isFinite(replyId)) {
      const found = findReply(this.controller.getState().reviewGroups, replyId)
      if (!found) return

      this.uiState.editingReplyId = this.uiState.editingReplyId === replyId ? null : replyId
      if (this.uiState.editingReplyId === replyId) {
        this.uiState.editReplyDrafts[replyId] = found.reply.content || ""
        this.uiState.replyEditError = null
      } else {
        delete this.uiState.editReplyDrafts[replyId]
        this.uiState.replyEditError = null
      }
      this.render()
      return
    }

    if (action === "cancel-reply-edit" && Number.isFinite(replyId)) {
      delete this.uiState.editReplyDrafts[replyId]
      if (this.uiState.editingReplyId === replyId) {
        this.uiState.editingReplyId = null
      }
      this.render()
      return
    }

    if (action === "submit-reply" && Number.isFinite(reviewId)) {
      const content = (this.uiState.replyDrafts[reviewId] || "").trim()
      const user_email = this.resolveUserEmail(this.uiState.replyEmailDrafts[reviewId])
      if (!content || !user_email) return
      try {
        await this.controller.submitReply({
          parent_id: reviewId,
          content,
          user_email,
          ...(this.options.contentName ? { content_name: this.options.contentName } : {}),
          ...(this.options.userName ? { user_name: this.options.userName } : {})
        })
        this.uiState.replyDrafts[reviewId] = ""
        this.uiState.replyEmailDrafts[reviewId] = ""
        this.pushToast('Reply added')
        this.render()
      } catch (error) {
        this.pushToast(
          error instanceof Error ? error.message : 'Failed to add reply',
          'error'
        )
      }
      return
    }

    if (action === "delete-review" && Number.isFinite(reviewId)) {
      this.uiState.deleteConfirm = {
        kind: 'review',
        id: reviewId,
        title: 'Delete review?',
        message: 'This action will permanently remove the review and its replies.'
      }
      this.render()
      return
    }

    if (action === "delete-reply" && Number.isFinite(replyId)) {
      this.uiState.deleteConfirm = {
        kind: 'reply',
        id: replyId,
        title: 'Delete reply?',
        message: 'This action will permanently remove the reply.'
      }
      this.render()
      return
    }

    if (action === 'cancel-delete') {
      this.uiState.deleteConfirm = null
      this.render()
      return
    }

    if (action === 'confirm-delete') {
      const confirm = this.uiState.deleteConfirm
      if (!confirm) return
      this.uiState.deleteConfirm = null
      try {
        const userEmail = this.resolveUserEmail()
        if (!userEmail) {
          this.pushToast('Email is required to delete this item', 'error')
          return
        }
        if (confirm.kind === 'review') {
          await this.controller.deleteReview(confirm.id, userEmail)
          this.pushToast('Review deleted')
        } else {
          await this.controller.deleteReply(confirm.id, userEmail)
          this.pushToast('Reply deleted')
        }
      } catch (error) {
        this.pushToast(
          error instanceof Error ? error.message : 'Failed to delete item',
          'error'
        )
      }
      this.render()
      return
    }

    if (action === 'toast-close') {
      const toastId = Number(actionEl.dataset.toastId || "")
      if (Number.isFinite(toastId)) {
        this.clearToast(toastId)
      }
      return
    }
  }

  private handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null
    if (!target) return

    if (target.matches('[data-field=\"review-name\"]')) {
      this.uiState.form.name = target.value
      this.uiState.reviewFormError = null
      return
    }

    if (target.matches('[data-field=\"review-email\"]')) {
      this.uiState.form.email = target.value
      this.uiState.reviewFormError = null
      return
    }

    if (target.matches('[data-field=\"review-content\"]')) {
      this.uiState.form.content = target.value
      this.uiState.reviewFormError = null
      return
    }

    if (target.matches('[data-field=\"reply-content\"]')) {
      const reviewId = Number(target.dataset.reviewId || "")
      if (Number.isFinite(reviewId)) {
        this.uiState.replyDrafts[reviewId] = target.value
      }
      return
    }

    if (target.matches('[data-field=\"reply-email\"]')) {
      const reviewId = Number(target.dataset.reviewId || "")
      if (Number.isFinite(reviewId)) {
        this.uiState.replyEmailDrafts[reviewId] = target.value
      }
      return
    }

    if (target.matches('[data-field=\"review-edit-content\"]')) {
      const reviewId = Number(target.dataset.reviewId || "")
      if (Number.isFinite(reviewId)) {
        const draft = this.uiState.editReviewDrafts[reviewId]
        if (draft) {
          draft.content = target.value
          this.uiState.reviewEditError = null
        }
      }
      return
    }

    if (target.matches('[data-field=\"reply-edit-content\"]')) {
      const replyId = Number(target.dataset.replyId || "")
      if (Number.isFinite(replyId)) {
        this.uiState.editReplyDrafts[replyId] = target.value
        this.uiState.replyEditError = null
      }
    }
  }

  private handleSubmit = async (event: SubmitEvent) => {
    const form = event.target as HTMLFormElement | null
    if (!form) return
    event.preventDefault()
    if (this.isSubmitting) return
    this.isSubmitting = true

    try { await this._handleSubmitInner(form) } finally { this.isSubmitting = false }
  }

  private _handleSubmitInner = async (form: HTMLFormElement) => {
    const formType = form.dataset.form

    if (formType === "review") {
      const name = this.uiState.form.name.trim()
      const email = this.uiState.form.email.trim()
      const content = this.uiState.form.content.trim()
      const rating = this.uiState.form.rating

      if (name.length < 2) {
        this.uiState.reviewFormError = 'Name is required'
        this.render()
        return
      }
      if (rating < 1 || rating > 5) {
        this.uiState.reviewFormError = 'Rating must be between 1 and 5'
        this.render()
        return
      }
      if (content.length < 10) {
        this.uiState.reviewFormError = 'Review content is required'
        this.render()
        return
      }

      this.uiState.reviewFormError = null
      try {
        const resolvedEmail = this.resolveUserEmail(email)
        if (!resolvedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resolvedEmail)) {
          this.uiState.reviewFormError = 'A valid email is required'
          this.render()
          return
        }
        await this.controller.submitReview({
          content_id: this.options.contentId,
          content_type: this.options.contentType?.trim() || 'business',
          ...(this.options.contentName ? { content_name: this.options.contentName } : {}),
          rating,
          content,
          user_email: resolvedEmail,
          ...(name ? { user_name: name } : {})
        })
        this.uiState.sessionEmail = resolvedEmail
        this.uiState.form = {
          name: this.options.userName ?? "",
          email: resolvedEmail,
          rating: 5,
          content: ""
        }
        this.uiState.showForm = false
        this.uiState.reviewFormError = null
        await this.controller.refresh()
        await this.loadSummary(true)
        this.pushToast('Review added')
      } catch (error) {
        this.pushToast(
          error instanceof Error ? error.message : 'Failed to add review',
          'error'
        )
      }
      return
    }

    if (formType === "review-edit") {
      const reviewId = Number(form.dataset.reviewId || "")
      const draft = Number.isFinite(reviewId) ? this.uiState.editReviewDrafts[reviewId] : null
      if (!Number.isFinite(reviewId) || !draft) return

      const content = draft.content.trim()
      if (draft.rating < 1 || draft.rating > 5) {
        this.uiState.reviewEditError = 'Rating must be between 1 and 5'
        this.render()
        return
      }
      if (content.length < 10) {
        this.uiState.reviewEditError = 'Review content must be at least 10 characters'
        this.render()
        return
      }

      try {
        await this.controller.updateReview(reviewId, {
          rating: draft.rating,
          content,
          user_email: this.resolveUserEmail(),
          ...(this.options.contentName ? { content_name: this.options.contentName } : {}),
          ...(this.options.userName ? { user_name: this.options.userName } : {})
        })
        await this.loadSummary(true)
        this.uiState.reviewEditError = null
        delete this.uiState.editReviewDrafts[reviewId]
        if (this.uiState.editingReviewId === reviewId) {
          this.uiState.editingReviewId = null
        }
        this.pushToast('Review updated')
        this.render()
      } catch (error) {
        this.pushToast(
          error instanceof Error ? error.message : 'Failed to update review',
          'error'
        )
      }
      return
    }

    if (formType === "reply") {
      const reviewId = Number(form.dataset.reviewId || "")
      if (!Number.isFinite(reviewId)) return
      const content = (this.uiState.replyDrafts[reviewId] || "").trim()
      const user_email = this.resolveUserEmail(this.uiState.replyEmailDrafts[reviewId])
      if (!content || !user_email) return
      await this.controller.submitReply({
        parent_id: reviewId,
        content,
        user_email,
        ...(this.options.contentName ? { content_name: this.options.contentName } : {}),
        ...(this.options.userName ? { user_name: this.options.userName } : {})
      })
      this.uiState.replyDrafts[reviewId] = ""
      this.uiState.replyEmailDrafts[reviewId] = ""
      this.render()
      return
    }

    if (formType === "reply-edit") {
      const replyId = Number(form.dataset.replyId || "")
      const content = Number.isFinite(replyId) ? (this.uiState.editReplyDrafts[replyId] || "").trim() : ""
      if (!Number.isFinite(replyId) || !content) {
        this.uiState.replyEditError = 'Reply content is required'
        this.render()
        return
      }

      try {
        await this.controller.updateReply(replyId, {
          content,
          user_email: this.resolveUserEmail(),
          ...(this.options.contentName ? { content_name: this.options.contentName } : {}),
          ...(this.options.userName ? { user_name: this.options.userName } : {})
        })
        this.uiState.replyEditError = null
        delete this.uiState.editReplyDrafts[replyId]
        if (this.uiState.editingReplyId === replyId) {
          this.uiState.editingReplyId = null
        }
        this.pushToast('Reply updated')
        this.render()
      } catch (error) {
        this.pushToast(
          error instanceof Error ? error.message : 'Failed to update reply',
          'error'
        )
      }
      return
    }
  }

  private async loadSummary(force = false) {
    if (this.uiState.summary && !force) return

    try {
      this.uiState.summary = await this.client.fetchBusinessSummary(this.options.contentId)
      this.render()
    } catch {
      this.uiState.summary = null
    }
  }

  private renderSummary() {
    const summary = this.uiState.summary
    if (!summary) return ''

    const average = Number(summary.reviews_avg_rating || 0).toFixed(1)
    const total = summary.reviews_count || 0

    const distRows = [5, 4, 3, 2, 1]
      .map((stars) => {
        const count = Number(summary.rating_breakdown?.[stars] || 0)
        const percent = total > 0 ? Math.round((count / total) * 100) : 0
        return `
          <div class="rrw-dist-row">
            <div class="rrw-dist-label">
              ${ratingLabelMarkup(stars)}
            </div>
            <div class="rrw-dist-bar"><div class="rrw-dist-fill" style="width:${percent}%"></div></div>
            <div>${count}</div>
          </div>
        `
      })
      .join('')

    return `
      <section class="rrw-summary">
        <div class="rrw-avg">
          <div class="rrw-avg-num">${average}</div>
          <div class="rrw-stars" aria-label="Average rating">
            ${starMarkup(Math.round(Number(summary.reviews_avg_rating)), 'rrw-star', 18)}
          </div>
          <div class="rrw-subtle">Based on ${total} reviews</div>
        </div>
        <div class="rrw-dist">${distRows}</div>
      </section>
    `
  }

  private renderReview(review: Review) {
    const userEmail = this.resolveUserEmail()
    const hasUserEmail = !!userEmail
    const isReviewOwner =
      !!userEmail && !!review.user_email &&
      userEmail.toLowerCase() === review.user_email.toLowerCase()
    const isEditingReview = this.uiState.editingReviewId === review.id
    const replies = review.replies || []
    const repliesHtml = replies.map((reply) => this.renderReply(review, reply)).join("")
    const reviewContent = (review.content || '').trim()
    const hasLongContent = reviewContent.length > 220
    const previewContent = hasLongContent ? truncateText(reviewContent) : reviewContent

    const reviewEditHtml = isEditingReview
      ? `
            <form class="rrw-form rrw-edit-form" data-form="review-edit" data-review-id="${review.id}">
              <div class="rrw-form-grid">
                ${this.uiState.reviewEditError ? `<div class="rrw-error">${escapeHtml(this.uiState.reviewEditError)}</div>` : ''}
                <div>
                  <label class="rrw-label">Rating</label>
                  <div class="rrw-rating-pick" role="radiogroup" aria-label="Edit rating">
                  ${renderRatingPickerMarkup(
                    this.uiState.editReviewDrafts[review.id]?.rating ?? Number(review.rating || 0),
                    "set-review-edit-rating",
                    review.id
                  )}
                </div>
              </div>
              <div>
                <label class="rrw-label">Your Review</label>
                <textarea class="rrw-textarea" data-field="review-edit-content" data-review-id="${review.id}" placeholder="Share your experience..." rows="4">${escapeHtml(this.uiState.editReviewDrafts[review.id]?.content ?? (review.content || ""))}</textarea>
              </div>
              <div class="rrw-actions">
                <button type="button" class="rrw-btn rrw-btn--ghost" data-action="cancel-review-edit" data-review-id="${review.id}">Cancel</button>
                <button type="submit" class="rrw-btn rrw-btn--primary">Save Review</button>
              </div>
            </div>
          </form>
        `
      : ""

    const replyFormHtml =
      !isEditingReview && this.uiState.expandedReplyReviewId === review.id
        ? `
          <form class="rrw-reply-form" data-form="reply" data-review-id="${review.id}">
            <textarea
              class="rrw-textarea"
              data-field="reply-content"
              data-review-id="${review.id}"
              placeholder="Write your reply..."
              rows="3"
            >${escapeHtml(this.uiState.replyDrafts[review.id] || "")}</textarea>
            ${!this.resolveUserEmail() ? `<input class="rrw-input" data-field="reply-email" data-review-id="${review.id}" type="email" value="${escapeHtml(this.uiState.replyEmailDrafts[review.id] || "")}" placeholder="your@email.com" />` : ''}
            <div class="rrw-actions">
              <button type="button" class="rrw-btn rrw-btn--ghost" data-action="toggle-replies" data-review-id="${review.id}">Cancel</button>
              <button type="button" class="rrw-btn rrw-btn--primary" data-action="submit-reply" data-review-id="${review.id}">Send Reply</button>
            </div>
          </form>
        `
        : ""

    const actionsHtml = isEditingReview
      ? ""
      : `
          <div class="rrw-review-actions">
            ${hasUserEmail ? `<button type="button" class="rrw-action rrw-action--icon" data-action="like-review" data-review-id="${review.id}" aria-label="Helpful" title="Helpful">
              ${actionIconMarkup("like")}
              <span class="rrw-action__count">${review.likes_count ?? 0}</span>
            </button>` : ""}
            ${hasUserEmail ? `<button type="button" class="rrw-action rrw-action--icon" data-action="dislike-review" data-review-id="${review.id}" aria-label="Not helpful" title="Not helpful">
              ${actionIconMarkup("dislike")}
              <span class="rrw-action__count">${review.dislikes_count ?? 0}</span>
            </button>` : ""}
            ${hasUserEmail ? `<button type="button" class="rrw-action rrw-action--icon" data-action="toggle-replies" data-review-id="${review.id}" aria-label="Reply" title="Reply">
              ${actionIconMarkup("reply")}
              ${replies.length ? `<span class="rrw-action__count">${replies.length}</span>` : ""}
            </button>` : ""}
            ${isReviewOwner ? `<button type="button" class="rrw-action rrw-action--icon" data-action="toggle-review-edit" data-review-id="${review.id}" aria-label="Edit review" title="Edit review">
              ${actionIconMarkup("edit")}
            </button>` : ""}
            ${isReviewOwner ? `<button type="button" class="rrw-action rrw-action--icon rrw-action--danger" data-action="delete-review" data-review-id="${review.id}" aria-label="Delete review" title="Delete review">${actionIconMarkup("delete")}</button>` : ""}
          </div>
        `

    const replySectionHtml = isEditingReview
      ? replyFormHtml
      : this.uiState.expandedReplyReviewId === review.id
        ? `<div class="rrw-replies">${repliesHtml}${replyFormHtml}</div>`
        : replies.length
          ? `<div class="rrw-replies">${repliesHtml}</div>`
          : replyFormHtml

    return `
      <article class="rrw-item">
        <div class="rrw-avatar">${escapeHtml(initials(review.user?.name || "U"))}</div>
        <div>
          <div class="rrw-top">
            <div>
              <div class="rrw-name">${escapeHtml(review.user?.name || "Anonymous")}</div>
              <div class="rrw-date">${escapeHtml(formatDate(review.created_at))}</div>
            </div>
            <div class="rrw-stars" aria-label="Review rating">
              ${starMarkup(Number(review.rating || 0), "rrw-star", 16)}
            </div>
          </div>
          ${
            isEditingReview
              ? reviewEditHtml
              : `<p class="rrw-body">${escapeHtml(previewContent)}${hasLongContent ? ` <button type="button" class="rrw-inline-link" data-action="open-review-modal" data-review-id="${review.id}">Read more</button>` : ""}${review.is_edited ? ' <span class="rrw-subtle">(edited)</span>' : ''}</p>`
          }
          <div class="rrw-meta">${actionsHtml}</div>
          ${replySectionHtml}
        </div>
      </article>
    `
  }

  private renderReply(review: Review, reply: ReviewReply) {
    const userEmail = this.resolveUserEmail()
    const hasUserEmail = !!userEmail
    const isReplyOwner =
      !!userEmail && !!reply.user_email &&
      userEmail.toLowerCase() === reply.user_email.toLowerCase()
    const isEditingReply = this.uiState.editingReplyId === reply.id

    if (isEditingReply) {
      return `
        <div class="rrw-reply">
          <div class="rrw-reply-avatar">${escapeHtml(initials(reply.user?.name || "U"))}</div>
          <div>
            <div class="rrw-top" style="margin-bottom: 0;">
              <div>
                <div class="rrw-name">${escapeHtml(reply.user?.name || "Anonymous")}</div>
                <div class="rrw-date">${escapeHtml(formatDate(reply.created_at))}</div>
              </div>
            </div>
            <form class="rrw-reply-form" data-form="reply-edit" data-reply-id="${reply.id}">
              <div class="rrw-reply-modal">
                <div class="rrw-reply-modal__head">
                  <div>
                    <h3 class="rrw-reply-modal__title">Edit Reply</h3>
                    <p class="rrw-reply-modal__subtle">Refine the reply below and save when it’s ready.</p>
                  </div>
                  <button
                    type="button"
                    class="rrw-modal__close"
                    data-action="cancel-reply-edit"
                    data-reply-id="${reply.id}"
                    aria-label="Close reply editor"
                  >✕</button>
                </div>
                <div class="rrw-reply-modal__meta">
                  <span>Author: ${escapeHtml(reply.user?.name || "Anonymous")}</span>
                  <span>Date: ${escapeHtml(formatDate(reply.created_at))}</span>
                  ${reply.is_edited ? '<span>Previously edited</span>' : ''}
                </div>
                <div class="rrw-reply-modal__body">
                  ${this.uiState.replyEditError ? `<div class="rrw-error">${escapeHtml(this.uiState.replyEditError)}</div>` : ''}
                  <label class="rrw-label" for="reply-edit-content-${reply.id}">Reply message</label>
                  <textarea
                    id="reply-edit-content-${reply.id}"
                    class="rrw-textarea"
                    data-field="reply-edit-content"
                    data-reply-id="${reply.id}"
                    placeholder="Edit your reply..."
                    rows="6"
                  >${escapeHtml(this.uiState.editReplyDrafts[reply.id] ?? (reply.content || ""))}</textarea>
                </div>
                <div class="rrw-actions">
                  <button type="button" class="rrw-btn rrw-btn--ghost" data-action="cancel-reply-edit" data-reply-id="${reply.id}">Cancel</button>
                  <button type="submit" class="rrw-btn rrw-btn--primary">Save Reply</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      `
    }

    return `
      <div class="rrw-reply">
        <div class="rrw-reply-avatar">${escapeHtml(initials(reply.user?.name || "U"))}</div>
        <div>
          <div class="rrw-top" style="margin-bottom: 0;">
            <div>
              <div class="rrw-name">${escapeHtml(reply.user?.name || "Anonymous")}</div>
              <div class="rrw-date">${escapeHtml(formatDate(reply.created_at))}</div>
            </div>
          </div>
          <div class="rrw-reply-text">${escapeHtml(reply.content || "")}${reply.is_edited ? ' <span class="rrw-subtle">(edited)</span>' : ''}</div>
          ${isReplyOwner ? `<div class="rrw-reply-actions"><button type="button" class="rrw-action rrw-action--icon" data-action="toggle-reply-edit" data-reply-id="${reply.id}" aria-label="Edit reply" title="Edit reply">${actionIconMarkup("edit")}</button><button type="button" class="rrw-action rrw-action--icon rrw-action--danger" data-action="delete-reply" data-reply-id="${reply.id}" aria-label="Delete reply" title="Delete reply">${actionIconMarkup("delete")}</button></div>` : ""}
        </div>
      </div>
    `
  }

  private render() {
    if (this.destroyed) return

    const state = this.controller.getState()

    const reviewsHtml = state.reviewGroups
      .flatMap((group) => group.reviews)
      .map((review) => this.renderReview(review))
      .join('')

    const loadingHtml = state.loading
      ? `<div class="rrw-loading">Loading reviews...</div>`
      : ''

    const errorHtml = state.error ? `<div class="rrw-error">${escapeHtml(state.error)}</div>` : ''

    const formHtml = this.uiState.showForm
      ? `
        <form class="rrw-form" data-form="review">
          ${this.uiState.reviewFormError ? `<div class="rrw-error">${escapeHtml(this.uiState.reviewFormError)}</div>` : ''}
          <div class="rrw-form-grid">
            <div>
              <label class="rrw-label">Your Name</label>
              <input class="rrw-input" data-field="review-name" type="text" value="${escapeHtml(this.uiState.form.name)}" placeholder="Enter your name" ${this.options.userName ? 'readonly style="opacity:0.7;cursor:default;"' : ''} />
            </div>
            <div>
              <label class="rrw-label">Email Address</label>
              <input class="rrw-input" data-field="review-email" type="email" value="${escapeHtml(this.uiState.form.email)}" placeholder="your@email.com" ${(this.options.user_email || this.options.userEmail) ? 'readonly style="opacity:0.7;cursor:default;"' : ''} />
            </div>
            <div>
              <label class="rrw-label">Rating</label>
              <div class="rrw-rating-pick" role="radiogroup" aria-label="Rating">
                ${Array.from({ length: 5 }, (_, index) => {
                  const rating = index + 1
                  const active = rating <= this.uiState.form.rating
                  return `
                    <button type="button" class="rrw-rating-btn" data-action="set-rating" data-rating="${rating}" aria-label="Rate ${rating} stars">
                      <span class="rrw-star ${active ? 'rrw-star--on' : 'rrw-star--off'}">
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                          <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"></path>
                        </svg>
                      </span>
                    </button>
                  `
                }).join('')}
              </div>
            </div>
            <div>
              <label class="rrw-label">Your Review</label>
              <textarea class="rrw-textarea" data-field="review-content" placeholder="Share your experience...">${escapeHtml(this.uiState.form.content)}</textarea>
            </div>
            <div class="rrw-actions">
              <button type="button" class="rrw-btn rrw-btn--ghost" data-action="toggle-form">Cancel</button>
              <button type="submit" class="rrw-btn rrw-btn--primary"${this.isSubmitting ? ' disabled style="opacity:0.65;cursor:not-allowed;"' : ''}>${this.isSubmitting ? 'Submitting…' : 'Submit Review'}</button>
            </div>
          </div>
        </form>
      `
      : ''

    const moreHtml =
      state.loadingMore
        ? `<div class="rrw-more"><button type="button" class="rrw-btn rrw-btn--ghost" disabled>Loading...</button></div>`
        : state.currentPage < state.lastPage
          ? `<div class="rrw-more"><button type="button" class="rrw-btn rrw-btn--ghost" data-action="load-more">See More Reviews</button></div>`
          : ''

    const deleteConfirmHtml = this.uiState.deleteConfirm
      ? `
        <div class="rrw-modal-overlay" data-action="cancel-delete">
          <div class="rrw-modal" role="dialog" aria-modal="true" aria-label="${escapeHtml(this.uiState.deleteConfirm.title)}">
            <div class="rrw-modal__head">
              <h3 class="rrw-modal__title">${escapeHtml(this.uiState.deleteConfirm.title)}</h3>
              <button type="button" class="rrw-modal__close" data-action="cancel-delete" aria-label="Close">x</button>
            </div>
            <p class="rrw-subtle" style="margin: 0 0 18px; font-size: 15px; line-height: 1.6;">${escapeHtml(this.uiState.deleteConfirm.message)}</p>
            <div class="rrw-actions" style="justify-content: flex-end;">
              <button type="button" class="rrw-btn rrw-btn--ghost" data-action="cancel-delete">Cancel</button>
              <button type="button" class="rrw-btn rrw-btn--danger" data-action="confirm-delete">Delete</button>
            </div>
          </div>
        </div>
      `
      : ''

    const toastHtml = this.uiState.toasts.length
      ? `
        <div class="rrw-toast-stack" aria-live="polite" aria-atomic="true">
          ${this.uiState.toasts
            .map(
              (toast) => `
                <div class="rrw-toast rrw-toast--${toast.type}">
                  <span class="rrw-toast__icon" aria-hidden="true">
                    <i class="${toast.type === 'success' ? 'las la-check-circle' : 'las la-exclamation-circle'}"></i>
                  </span>
                  <div class="rrw-toast__message">${escapeHtml(toast.message)}</div>
                  <button type="button" class="rrw-toast__close" data-action="toast-close" data-toast-id="${toast.id}" aria-label="Dismiss notification">×</button>
                </div>
              `
            )
            .join('')}
        </div>
      `
      : ''

    this.root.innerHTML = `
      <section class="rrw-card">
        <div class="rrw-shell">
          <div class="rrw-head">
            <h3 class="rrw-title">${escapeHtml(this.options.title || 'Reviews')}</h3>
            <button type="button" class="rrw-cta" data-action="toggle-form">${this.uiState.showForm ? 'Close form' : 'Write a Review'}</button>
          </div>
          ${this.renderSummary()}
          ${errorHtml}
          ${formHtml}
          ${loadingHtml}
          <div class="rrw-list">
            ${reviewsHtml || (!state.loading ? `<div class="rrw-empty">No reviews yet. Be the first to review.</div>` : '')}
          </div>
          ${moreHtml}
        </div>
      </section>
      ${deleteConfirmHtml}
      ${toastHtml}
    `
  }

  async refresh() {
    await this.controller.refresh()
    await this.loadSummary(true)
  }

  unmount() {
    if (this.destroyed) return
    this.destroyed = true
    for (const timer of this.toastTimers.values()) {
      window.clearTimeout(timer)
    }
    this.toastTimers.clear()
    this.unsubscribe()
    this.unbindEvents()
    this.root.innerHTML = ''
    this.root.classList.remove('rrw-widget')
  }
}

export function mountReviewWidget(
  target: string | Element | ArrayLike<Element>,
  options: ReviewWidgetOptions
): ReviewWidgetHandle[] {
  if (typeof document === 'undefined') {
    throw new Error('mountReviewWidget can only run in a browser environment')
  }

  const elements: Element[] =
    typeof target === 'string'
      ? Array.from(document.querySelectorAll(target))
      : 'length' in target
        ? Array.from(target)
        : [target]

  if (elements.length === 0) {
    throw new Error(`No widget containers found for target: ${String(target)}`)
  }

  const resolvedOptions = {
    ...options,
    baseUrl: resolveBaseUrl(options.baseUrl)
  }

  return elements.map((element) => {
    if (!(element instanceof HTMLElement)) {
      throw new Error('Review widget target must be an HTMLElement')
    }
    if (element.classList.contains('rrw-widget')) {
      return { unmount: () => {}, refresh: async () => {} }
    }
    return new ReviewWidgetInstance(resolvedOptions, element)
  })
}

export function mountReviewWidgetFromDataAttributes(
  target: string | Element | ArrayLike<Element>,
  overrides: Partial<Omit<ReviewWidgetOptions, 'contentId'>> = {}
): ReviewWidgetHandle[] {
  if (typeof document === 'undefined') {
    throw new Error('mountReviewWidgetFromDataAttributes can only run in a browser environment')
  }

  const elements: Element[] =
    typeof target === 'string'
      ? Array.from(document.querySelectorAll(target))
      : 'length' in target
        ? Array.from(target)
        : [target]

  const handles: ReviewWidgetHandle[] = []

  for (const element of elements) {
    const data = parseDataAttributes(element)
    if (!data) continue

    if (!(element instanceof HTMLElement)) {
      throw new Error('Review widget target must be an HTMLElement')
    }

      const handle = new ReviewWidgetInstance(
      {
        ...(data.baseUrl ? { baseUrl: data.baseUrl } : {}),
        contentId: data.contentId,
        ...(data.contentType ? { contentType: data.contentType } : {}),
        ...(data.title ? { title: data.title } : {}),
        ...(data.pageSize ? { pageSize: data.pageSize } : {}),
        ...(data.userEmail ? { user_email: data.userEmail } : {}),
        ...overrides
      },
      element
    )

    handles.push(handle)
  }

  return handles
}

function resolveBaseUrl(baseUrl?: string) {
  const resolved = (baseUrl || DEFAULT_BASE_URL).trim().replace(/\/$/, '')
  if (!resolved) {
    throw new Error(
      'Review widget base URL is not configured. Pass `baseUrl` to mountReviewWidget(...) or set REVIEW_WIDGET_BASE_URL in the heldsway_sdk build environment.'
    )
  }
  return resolved
}

export function autoMountReviewWidgets(selector = AUTO_MOUNT_SELECTOR) {
  if (typeof document === 'undefined') return []
  const handles = mountReviewWidgetFromDataAttributes(selector)
  for (const handle of handles) {
    autoMountedHandles.add(handle)
  }
  return handles
}

export function unmountAutoMountedReviewWidgets() {
  for (const handle of autoMountedHandles) {
    handle.unmount()
  }
  autoMountedHandles.clear()
}

export function installReviewWidgetGlobalApi() {
  if (typeof window === 'undefined') return

  window.ReviewWidget = {
    mount: mountReviewWidget,
    autoMount: autoMountReviewWidgets,
    mountFromDataAttributes: mountReviewWidgetFromDataAttributes,
    unmountAll: unmountAutoMountedReviewWidgets
  }
  window.mountReviewWidget = mountReviewWidget
  window.mountReviewWidgetFromDataAttributes = mountReviewWidgetFromDataAttributes

  const runAutoMount = () => {
    autoMountReviewWidgets()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAutoMount, { once: true })
  } else {
    runAutoMount()
  }
}

